<template>
  <div class="doubao-chat-component">
    <div class="chat-container" ref="chatContainer">
      <!-- 欢迎语 -->
      <div class="chat-message ai-message">
        <div class="avatar ai-avatar"></div>
        <div class="message-content">{{ welcomeText }}</div>
      </div>

      <!-- 聊天记录 -->
      <div class="chat-message"
           :class="{ 'user-message': msg.role === 'user', 'ai-message': msg.role === 'ai' }"
           v-for="(msg, index) in messages"
           :key="index">
        <div class="avatar"
             :class="{ 'user-avatar': msg.role === 'user', 'ai-avatar': msg.role === 'ai' }"></div>
        <div class="message-content">
          <div v-if="msg.loading && !msg.isTyping" class="loading-dots">
            <span>.</span><span>.</span><span>.</span>
          </div>
          <div v-else class="message-text" v-html="formatMessage(msg.content)"></div>
          <div v-if="msg.error" class="error-text">{{ msg.error }}</div>
        </div>
      </div>
    </div>

    <!-- 输入区域 -->
    <div class="input-area">
      <textarea v-model="inputContent"
                :placeholder="inputPlaceholder"
                class="message-input"
                @keyup.enter="handleSendMessage($event)"
                :disabled="loading"></textarea>
      <button class="send-btn"
              @click="handleSendMessage"
              :disabled="loading || !inputContent.trim()">
        {{ loading ? '发送中...' : '发送' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DoubaoChat',
  props: {
    welcomeText: {
      type: String,
      default: '欢迎使用聊天机器人'
    },
    inputPlaceholder: {
      type: String,
      default: '请输入你的问题...（回车发送）'
    },
    typingSpeed: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      inputContent: '',
      messages: [],
      loading: false,
      typingTimer: null,
      currentAiIndex: -1,
      isStreaming: false,
      eventSource: null
    };
  },
  beforeDestroy() {
    this.cleanupStream();
  },
  methods: {
    handleSendMessage(e) {
      if (e && e.key === 'Enter') {
        if (!e.ctrlKey) {
          e.preventDefault();
        } else {
          return;
        }
      }

      const content = this.inputContent.trim();
      if (!content || this.loading) return;

      // 清理旧的连接
      this.cleanupStream();

      // 添加用户消息
      this.messages.push({
        role: 'user',
        content: content,
        loading: false,
        error: ''
      });
      
      this.inputContent = '';
      
      // 添加AI消息占位
      const aiMsgIndex = this.messages.push({
        role: 'ai',
        content: '',
        loading: true,
        isTyping: false,
        error: ''
      }) - 1;
      
      this.currentAiIndex = aiMsgIndex;
      this.sendStreamMessage(content, aiMsgIndex);
    },

    cleanupStream() {
      // 清理打字机定时器
      if (this.typingTimer) {
        clearInterval(this.typingTimer);
        this.typingTimer = null;
      }
      
      // 清理EventSource
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      
      this.isStreaming = false;
    },

    async sendStreamMessage(question, index) {
      this.loading = true;
      this.isStreaming = true;

      try {
        const response = await fetch('http://192.168.1.197:9094/web/stream.html', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream',
            'Cache-Control': 'no-cache',
            'Authorization': `Bearer 128cb1b4-5ce3-466c-934a-43e897f4808b`
          },
          body: JSON.stringify({ query: question })
        });

        if (!response.ok) {
          throw new Error(`HTTP错误! 状态码: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let isProcessing = true;

        // 显示初始的打字机状态
        this.$set(this.messages[index], 'isTyping', true);

        while (isProcessing) {
          const { done, value } = await reader.read();
          
          if (done) {
            isProcessing = false;
            this.$set(this.messages[index], 'loading', false);
            this.$set(this.messages[index], 'isTyping', false);
            
            // 触发完成事件
            this.$emit('message-success', {
              question: question,
              answer: this.messages[index].content
            });
            break;
          }

          // 解码数据
          buffer += decoder.decode(value, { stream: true });
          
          // 按行分割处理SSE格式
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim()) continue; // 跳过空行
            
            // 处理 data: 格式
            if (line.startsWith('data:')) {
              const dataContent = line.slice(5).trim(); // 去掉 "data: " 前缀
              
              // 忽略完成标记
              if (dataContent === 'complete') continue;
              
              // 处理实际内容
              if (dataContent) {
                // 直接显示内容（如果需要打字机效果，可以调用 typeText 方法）
                this.displayText(index, dataContent);
              }
            }
            
            // 处理 event: 格式
            if (line.startsWith('event:')) {
              const eventType = line.slice(6).trim();
              if (eventType === 'done') {
                isProcessing = false;
                this.$set(this.messages[index], 'loading', false);
                this.$set(this.messages[index], 'isTyping', false);
                
                this.$emit('message-success', {
                  question: question,
                  answer: this.messages[index].content
                });
              }
            }
          }
        }
      } catch (error) {
        console.error('SSE流错误:', error);
        this.handleError(index, error.message || '网络连接错误');
      } finally {
        this.loading = false;
        this.isStreaming = false;
      }
    },

    // 直接显示文本（如果不需要打字机效果）
    displayText(index, text) {
      if (!text) return;
      
      // 累积显示文本
      const currentContent = this.messages[index].content || '';
      const newContent = currentContent + text;
      
      this.$set(this.messages[index], 'content', newContent);
      
      // 自动滚动
      this.$nextTick(() => {
        const container = this.$refs.chatContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    // 打字机效果显示（如果需要）
    typeText(index, text) {
      if (!text || !this.isStreaming) return;
      
      // 清理旧的定时器
      if (this.typingTimer) clearInterval(this.typingTimer);
      
      const chars = text.split('');
      let charIndex = 0;
      let currentContent = this.messages[index].content || '';
      
      this.typingTimer = setInterval(() => {
        if (charIndex < chars.length) {
          currentContent += chars[charIndex];
          this.$set(this.messages[index], 'content', currentContent);
          charIndex++;
          
          // 自动滚动
          this.$nextTick(() => {
            const container = this.$refs.chatContainer;
            if (container) {
              container.scrollTop = container.scrollHeight;
            }
          });
        } else {
          clearInterval(this.typingTimer);
          this.typingTimer = null;
        }
      }, this.typingSpeed);
    },

    handleError(index, errorMsg) {
      this.$set(this.messages[index], 'loading', false);
      this.$set(this.messages[index], 'isTyping', false);
      this.$set(this.messages[index], 'error', errorMsg);
      this.$emit('message-error', errorMsg);
    },

    formatMessage(content) {
      if (!content) return '';
      // 将换行符转换为HTML换行
      return content.replace(/\n/g, '<br>');
    },

    clearChat() {
      this.cleanupStream();
      this.messages = [];
      this.inputContent = '';
      this.currentAiIndex = -1;
    }
  }
};
</script>

<style scoped>
/* 样式保持不变 */
.doubao-chat-component {
  width: 100%;
  max-width: 500px;
  height: 600px;
  margin: 0 auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background-color: #f9fafb;
  font-family: 'Microsoft Yahei', sans-serif;
}

.chat-container {
  height: calc(100% - 100px);
  padding: 15px;
  overflow-y: auto;
  box-sizing: border-box;
}

.chat-message {
  display: flex;
  margin-bottom: 15px;
  max-width: 90%;
}

.user-message {
  flex-direction: row-reverse;
  margin-left: auto;
}

.ai-message {
  flex-direction: row;
  margin-right: auto;
}

.avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.user-avatar {
  background-color: #4299e1;
  color: white;
  margin-left: 10px;
}

.ai-avatar {
  background-color: #f3f4f6;
  margin-right: 10px;
}

.message-content {
  padding: 10px 15px;
  border-radius: 18px;
  line-height: 1.6;
  font-size: 14px;
  white-space: pre-wrap;
  position: relative;
  min-height: 20px;
}

.user-message .message-content {
  background-color: #4299e1;
  color: white;
  border-bottom-right-radius: 4px;
}

.ai-message .message-content {
  background-color: white;
  color: #334155;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
}

.loading-dots {
  display: flex;
  gap: 4px;
  align-items: center;
  height: 20px;
}

.loading-dots span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #94a3b8;
  animation: loading 1.4s infinite ease-in-out both;
}

.loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes loading {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.message-text {
  white-space: pre-wrap;
}

.error-text {
  color: #ef4444;
  font-size: 12px;
  margin-top: 5px;
}

.input-area {
  height: 100px;
  padding: 15px;
  background-color: white;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 10px;
  box-sizing: border-box;
}

.message-input {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 15px;
  outline: none;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
  height: 70px;
  box-sizing: border-box;
}

.message-input:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 2px rgba(66, 153, 225, 0.1);
}

.message-input:disabled {
  background-color: #f9fafb;
  cursor: not-allowed;
}

.send-btn {
  width: 80px;
  background-color: #4299e1;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.send-btn:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
}

.chat-container::-webkit-scrollbar {
  width: 4px;
}

.chat-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.chat-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

/* 打字光标效果 */
.ai-message .message-content.is-typing::after {
  content: '|';
  position: absolute;
  right: -5px;
  bottom: 10px;
  color: #94a3b8;
  animation: blink 0.8s infinite;
  font-weight: bold;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}
</style>